import { FC, useMemo } from 'react';
import { Heading } from 'roo-ui/components';
import { sanitizeHTML } from 'lib/utils';
import { DetailsBooking } from 'types';
import { BreakOnNewLine, ContentHeading } from 'components/Typography';
import CollapsableCard from 'components/CollapsableCard';
import MessageBoard from 'components/MessageBoard';
import { areEmpty, isAbove400CharactersLimit } from './helpers';
import { MaxWidthContent } from './primitives';

interface KnowBeforeYouGoProps {
  booking: DetailsBooking;
  onToggle: () => any;
}

const KnowBeforeYouGo: FC<KnowBeforeYouGoProps> = ({ booking, onToggle }) => {
  const { checkInInstructions, knowBeforeYouGoDescription, mandatoryFeesDescription } = useMemo(
    () => ({
      checkInInstructions: sanitizeHTML(booking.reservation.property.checkInInstructions),
      knowBeforeYouGoDescription: sanitizeHTML(booking.reservation.property.knowBeforeYouGoDescription),
      mandatoryFeesDescription: sanitizeHTML(booking.reservation.property.mandatoryFeesDescription),
    }),
    [booking],
  );

  if (
    booking.reservation.isAfterCheckOut ||
    !booking.flags.isBooked ||
    areEmpty(checkInInstructions, knowBeforeYouGoDescription, mandatoryFeesDescription)
  ) {
    return null;
  }

  const showHotelPolicies = !areEmpty(knowBeforeYouGoDescription, mandatoryFeesDescription);
  const shouldCollapse = isAbove400CharactersLimit(checkInInstructions, knowBeforeYouGoDescription, mandatoryFeesDescription);

  return (
    <>
      <ContentHeading>Know before you go</ContentHeading>

      <MessageBoard mb="5" mt="-8px">
        <strong>Please note,</strong> there is important information to know before checking in.
      </MessageBoard>

      <CollapsableCard onToggle={onToggle} collapsable={shouldCollapse}>
        <MaxWidthContent>
          {checkInInstructions && (
            <>
              <Heading.h4 mt={6} data-testid="check-in-instructions-heading">
                Check-In Instructions
              </Heading.h4>
              <BreakOnNewLine dangerouslySetInnerHTML={{ __html: checkInInstructions }} />
            </>
          )}
          {showHotelPolicies && (
            <Heading.h4 mt={6} data-testid="hotel-policies-heading">
              Hotel Policies
            </Heading.h4>
          )}
          {knowBeforeYouGoDescription && (
            <>
              <Heading.h5 data-testid="know-before-you-go-heading">Know Before You Go</Heading.h5>
              <BreakOnNewLine dangerouslySetInnerHTML={{ __html: knowBeforeYouGoDescription }} />
            </>
          )}
          {mandatoryFeesDescription && (
            <>
              <Heading.h5 data-testid="mandatory-fees-heading">Fees</Heading.h5>
              <BreakOnNewLine dangerouslySetInnerHTML={{ __html: mandatoryFeesDescription }} />
            </>
          )}
        </MaxWidthContent>
      </CollapsableCard>
    </>
  );
};

export default KnowBeforeYouGo;
